// Region-aware date/time formatting for the clinic.
//
// The clinic's clinical-availability configuration carries a timezone (the
// "Zona Horaria" set in Configuración, also returned by the availability/slots
// API). That timezone IS the region: when it changes, the displayed date format
// follows the conventions of that region's locale — e.g. Mexico City shows
// 31/12/2026, New York shows 12/31/2026, Tokyo shows 2026/12/31.

export const DEFAULT_CLINIC_TZ = 'America/Mexico_City'

// Maps each supported availability timezone to the locale whose conventional
// date format should be used for that region. Mirrors the timezone options
// offered in the availability settings (SettingsPage).
const REGION_LOCALE: Record<string, string> = {
  UTC: 'en-GB', // ISO-ish day/month/year, region-neutral
  'America/New_York': 'en-US',
  'America/Chicago': 'en-US',
  'America/Los_Angeles': 'en-US',
  'America/Mexico_City': 'es-MX',
  'America/Argentina/Buenos_Aires': 'es-AR',
  'Europe/London': 'en-GB',
  'Europe/Paris': 'fr-FR',
  'Europe/Madrid': 'es-ES',
  'Asia/Tokyo': 'ja-JP',
}

// The locale for a given availability timezone. Falls back to the default
// clinic region when the timezone is missing or unmapped.
export function regionLocale(timezone?: string): string {
  if (timezone && REGION_LOCALE[timezone]) return REGION_LOCALE[timezone]
  return REGION_LOCALE[DEFAULT_CLINIC_TZ]
}

// Format a date in the clinic's region: numeric day/month/year, ordered by the
// region's locale convention.
export function formatClinicDate(iso: string, timezone?: string): string {
  const timeZone = timezone || DEFAULT_CLINIC_TZ
  return new Intl.DateTimeFormat(regionLocale(timeZone), {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso))
}

// Format a time of day in the clinic's region.
export function formatClinicTime(iso: string, timezone?: string): string {
  const timeZone = timezone || DEFAULT_CLINIC_TZ
  return new Intl.DateTimeFormat(regionLocale(timeZone), {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}
